import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterAlt as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AccountBalance as AccountIcon,
  ContactPhone as ContactIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ContactsModal from '../components/providers/ContactsModal';
import AccountsModal from '../components/providers/AccountsModal';
import { providerService } from '../services/providerService';

// Función para formatear número de cuenta (separar cada 4 dígitos)
const formatAccountNumber = (value) => {
  // Remover espacios y caracteres no numéricos excepto letras (para IBAN)
  const cleaned = value.replace(/[^\w]/g, '');
  // Separar cada 4 caracteres con un espacio
  return cleaned.replace(/(.{4})/g, '$1 ').trim();
};

// Función para limpiar el formato del número de cuenta
const cleanAccountNumber = (value) => {
  return value.replace(/\s/g, '');
};

// Esquema de validación para el formulario de proveedor
const providerSchema = Yup.object({
  name: Yup.string().required('El nombre es obligatorio'),
  cif: Yup.string().required('El CIF es obligatorio'),
  address: Yup.string().required('La dirección es obligatoria'),
  postalCode: Yup.string().required('El código postal es obligatorio'),
  city: Yup.string().required('La ciudad es obligatoria'),
  email: Yup.string().email('Email inválido').required('El email es obligatorio'),
  phone: Yup.string().required('El teléfono es obligatorio'),
  contacts: Yup.array().of(
    Yup.object({
      name: Yup.string().required('El nombre del contacto es obligatorio'),
      email: Yup.string().email('Email inválido').required('El email del contacto es obligatorio'),
      phone: Yup.string().required('El teléfono del contacto es obligatorio')
    })
  ),
  accounts: Yup.array().of(
    Yup.object({
      number: Yup.string().required('El número de cuenta es obligatorio')
    })
  )
});

function Providers() {
  // Estados para filtros
  const [filters, setFilters] = useState({
    search: ''
  });
  
  // Estado para proveedores
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estado para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Estados para modales
  const [openProviderModal, setOpenProviderModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [openAccountsModal, setOpenAccountsModal] = useState(false);
  
  // Estados para feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [submitting, setSubmitting] = useState(false);

  // Cargar proveedores al montar el componente
  useEffect(() => {
    loadProviders();
  }, [page, rowsPerPage, filters.search]);

  // Función para cargar proveedores
  const loadProviders = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1, // Backend usa páginas 1-indexed
        limit: rowsPerPage,
        search: filters.search
      };

      const result = await providerService.getProviders(params);
      
      if (result.success) {
        setProviders(result.data.providers || []);
        setTotalCount(result.data.total || 0);
      } else {
        showSnackbar('Error al cargar proveedores: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al cargar proveedores', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para mostrar mensajes
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  // Manejadores para cambios en filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
    setPage(0); // Reset a la primera página cuando se filtra
  };
  
  // Manejadores para paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Manejadores para modales
  const handleOpenProviderModal = (provider = null) => {
    setSelectedProvider(provider);
    setOpenProviderModal(true);
  };
  
  const handleCloseProviderModal = () => {
    setOpenProviderModal(false);
    setSelectedProvider(null);
  };
  
  const handleOpenContactsModal = (provider) => {
    setSelectedProvider(provider);
    setOpenContactsModal(true);
  };
  
  const handleOpenAccountsModal = (provider) => {
    setSelectedProvider(provider);
    setOpenAccountsModal(true);
  };
  
  const handleSaveContacts = (contacts) => {
    if (selectedProvider) {
      setProviders(providers.map(p =>
        p.id === selectedProvider.id ? { ...p, contacts } : p
      ));
      // Recargar la lista para asegurar datos actualizados
      loadProviders();
    }
  };
  
  const handleSaveAccounts = (accounts) => {
    if (selectedProvider) {
      setProviders(providers.map(p =>
        p.id === selectedProvider.id ? { ...p, accounts } : p
      ));
      // Recargar la lista para asegurar datos actualizados
      loadProviders();
    }
  };

  // Configurar formulario con Formik
  const formik = useFormik({
    initialValues: {
      name: selectedProvider?.name || '',
      cif: selectedProvider?.cif || '',
      address: selectedProvider?.address || '',
      postalCode: selectedProvider?.postalCode || '',
      city: selectedProvider?.city || '',
      email: selectedProvider?.email || '',
      phone: selectedProvider?.phone || '',
      contacts: selectedProvider?.contacts || [],
      accounts: selectedProvider?.accounts || []
    },
    validationSchema: providerSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setSubmitting(true);
      
      try {
        // Limpiar datos antes de enviar
        const cleanedValues = {
          ...values,
          // Limpiar contactos: quitar IDs temporales y campos vacíos
          contacts: values.contacts
            .filter(contact => contact.name && contact.email && contact.phone)
            .map(contact => ({
              name: contact.name.trim(),
              email: contact.email.trim(),
              phone: contact.phone.trim()
            })),
          // Limpiar cuentas: quitar IDs temporales y campos vacíos
          accounts: values.accounts
            .filter(account => account.number)
            .map(account => ({
              number: cleanAccountNumber(account.number.trim())
            }))
        };

        console.log('Datos a enviar:', cleanedValues);
        
        let result;
        
        if (selectedProvider) {
          // Actualizar proveedor existente
          result = await providerService.updateProvider(selectedProvider.id, cleanedValues);
        } else {
          // Crear nuevo proveedor
          result = await providerService.createProvider(cleanedValues);
        }
        
        console.log('Resultado del backend:', result);
        
        if (result.success) {
          showSnackbar(
            selectedProvider ? 'Proveedor actualizado correctamente' : 'Proveedor creado correctamente'
          );
          handleCloseProviderModal();
          loadProviders(); // Recargar la lista
        } else {
          showSnackbar('Error al guardar proveedor: ' + result.message, 'error');
        }
      } catch (error) {
        console.error('Error en onSubmit:', error);
        showSnackbar('Error al guardar proveedor', 'error');
      } finally {
        setSubmitting(false);
      }
    }
  });
  
  // Manejadores para contactos y cuentas
  const handleAddContact = () => {
    formik.setFieldValue('contacts', [
      ...formik.values.contacts,
      { id: Date.now(), name: '', email: '', phone: '' }
    ]);
  };
  
  const handleRemoveContact = (index) => {
    const newContacts = [...formik.values.contacts];
    newContacts.splice(index, 1);
    formik.setFieldValue('contacts', newContacts);
  };
  
  const handleAddAccount = () => {
    formik.setFieldValue('accounts', [
      ...formik.values.accounts,
      { id: Date.now(), number: '' }
    ]);
  };
  
  const handleRemoveAccount = (index) => {
    const newAccounts = [...formik.values.accounts];
    newAccounts.splice(index, 1);
    formik.setFieldValue('accounts', newAccounts);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gestión de Proveedores</Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => handleOpenProviderModal()}
          sx={{ fontWeight: 'bold' }}
        >
          Nuevo Proveedor
        </Button>
      </Box>
      
      {/* Sección de filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          <FilterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Filtros
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              name="search"
              label="Buscar por nombre, CIF o email"
              value={filters.search}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
          </Grid>
        </Grid>
      </Paper>
      
      {/* Tabla de proveedores */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>CIF</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Ciudad</TableCell>
              <TableCell align="center">Contactos</TableCell>
              <TableCell align="center">Cuentas</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Cargando proveedores...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : providers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No se encontraron proveedores
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              providers.map((provider) => (
                <TableRow key={provider.id} hover>
                  <TableCell component="th" scope="row">
                    {provider.name}
                  </TableCell>
                  <TableCell>{provider.cif}</TableCell>
                  <TableCell>{provider.email}</TableCell>
                  <TableCell>{provider.phone}</TableCell>
                  <TableCell>{provider.city}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver contactos">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenContactsModal(provider)}
                      >
                        <ContactIcon />
                      </IconButton>
                    </Tooltip>
                    <Chip
                      size="small"
                      label={provider.contacts?.length || 0}
                      sx={{ ml: 1 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver cuentas">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenAccountsModal(provider)}
                      >
                        <AccountIcon />
                      </IconButton>
                    </Tooltip>
                    <Chip
                      size="small"
                      label={provider.accounts?.length || 0}
                      sx={{ ml: 1 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton 
                        size="small" 
                        color="secondary"
                        onClick={() => handleOpenProviderModal(provider)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
      
      {/* Modal para crear/editar/ver proveedor */}
      <Dialog
        open={openProviderModal}
        onClose={handleCloseProviderModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedProvider ? 'Editar Proveedor' : 'Nuevo Proveedor'}
        </DialogTitle>
        
        <DialogContent>
          <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Información básica */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Información Básica
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="name"
                  label="Nombre"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="cif"
                  label="CIF"
                  value={formik.values.cif}
                  onChange={formik.handleChange}
                  error={formik.touched.cif && Boolean(formik.errors.cif)}
                  helperText={formik.touched.cif && formik.errors.cif}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="address"
                  label="Dirección"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="postalCode"
                  label="Código Postal"
                  value={formik.values.postalCode}
                  onChange={formik.handleChange}
                  error={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
                  helperText={formik.touched.postalCode && formik.errors.postalCode}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="city"
                  label="Ciudad"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="email"
                  label="Email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="phone"
                  label="Teléfono"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                />
              </Grid>
              
              {/* Contactos */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                  <Typography variant="h6">
                    <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Personas de Contacto
                  </Typography>
                  
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddContact}
                    size="small"
                  >
                    Añadir Contacto
                  </Button>
                </Box>
                
                <List>
                  {formik.values.contacts.map((contact, index) => (
                    <React.Fragment key={contact.id}>
                      <ListItem>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="small"
                              name={`contacts.${index}.name`}
                              label="Nombre"
                              value={contact.name}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.contacts?.[index]?.name && 
                                Boolean(formik.errors.contacts?.[index]?.name)
                              }
                              helperText={
                                formik.touched.contacts?.[index]?.name && 
                                formik.errors.contacts?.[index]?.name
                              }
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="small"
                              name={`contacts.${index}.email`}
                              label="Email"
                              type="email"
                              value={contact.email}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.contacts?.[index]?.email && 
                                Boolean(formik.errors.contacts?.[index]?.email)
                              }
                              helperText={
                                formik.touched.contacts?.[index]?.email && 
                                formik.errors.contacts?.[index]?.email
                              }
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              size="small"
                              name={`contacts.${index}.phone`}
                              label="Teléfono"
                              value={contact.phone}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.contacts?.[index]?.phone && 
                                Boolean(formik.errors.contacts?.[index]?.phone)
                              }
                              helperText={
                                formik.touched.contacts?.[index]?.phone && 
                                formik.errors.contacts?.[index]?.phone
                              }
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton 
                              color="error" 
                              size="small"
                              onClick={() => handleRemoveContact(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </ListItem>
                      {index < formik.values.contacts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Grid>
              
              {/* Cuentas bancarias */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                  <Typography variant="h6">
                    <AccountIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Cuentas Bancarias
                  </Typography>
                  
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddAccount}
                    size="small"
                  >
                    Añadir Cuenta
                  </Button>
                </Box>
                
                <List>
                  {formik.values.accounts.map((account, index) => (
                    <React.Fragment key={account.id}>
                      <ListItem>
                        <Grid container spacing={2}>
                          <Grid item xs={11}>
                            <TextField
                              fullWidth
                              size="small"
                              name={`accounts.${index}.number`}
                              label="Número de Cuenta"
                              value={formatAccountNumber(account.number || '')}
                              onChange={(e) => {
                                const cleanedValue = cleanAccountNumber(e.target.value);
                                formik.setFieldValue(`accounts.${index}.number`, cleanedValue);
                              }}
                              error={
                                formik.touched.accounts?.[index]?.number && 
                                Boolean(formik.errors.accounts?.[index]?.number)
                              }
                              helperText={
                                formik.touched.accounts?.[index]?.number && 
                                formik.errors.accounts?.[index]?.number
                              }
                              placeholder="ES00 1234 5678 9012 3456 7890"
                            />
                          </Grid>
                          
                          <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton 
                              color="error" 
                              size="small"
                              onClick={() => handleRemoveAccount(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </ListItem>
                      {index < formik.values.accounts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseProviderModal}>
            Cancelar
          </Button>
          
          <Button 
            type="submit" 
            variant="contained" 
            onClick={formik.handleSubmit}
            disabled={submitting}
            startIcon={submitting && <CircularProgress size={20} />}
          >
            {submitting 
              ? 'Guardando...' 
              : (selectedProvider ? 'Guardar Cambios' : 'Crear Proveedor')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modales */}
      <ContactsModal
        open={openContactsModal}
        onClose={() => setOpenContactsModal(false)}
        provider={selectedProvider}
        onSave={handleSaveContacts}
      />

      <AccountsModal
        open={openAccountsModal}
        onClose={() => setOpenAccountsModal(false)}
        provider={selectedProvider}
        onSave={handleSaveAccounts}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Providers; 